<template>
  <SettingsPageLayout>
    <QuickstartCard v-if="$route.query.quickstart">
      <template #header>👋 Invite your teammates</template>
      <template #body>
        Invite your supporting teammates to Humaniz. These users are typically Recruiters, Sales Managers, Operations,
        etc. who help you manage your team.
      </template>
    </QuickstartCard>
    <DetailForm
      title="Invite New User"
      description="Invites a new user and adds them to your team as a Contributor."
      action="Send"
      hidecancel="true"
      data-intro="Let's get your first user added to your team. If you have a partner, recruiter, or sales manager, add them here."
      @handle-action="createUser()"
    >
      <div class="grid grid-cols-3 gap-x-4">
        <TextInput
          class="col-span-2"
          label="Email"
          placeholder="test@example.com"
          :model-value="user.email"
          @update:model-value="value => (user.email = value)"
        />
        <SelectMenu
          class="col-span-1"
          label="Role"
          initial-value="Admin"
          :options="['Admin', 'Contributor']"
          @selected-option="user.role = $event"
        />
      </div>
    </DetailForm>
    <div class="mt-6">
      <div v-if="users.length > 0" class="flex flex-col">
        <DataTable :columns="dtColumns" :rows="rows" :sort="sort">
          <template #td-email="slotProps">
            {{ slotProps.row.email }}
          </template>
          <template #td-assignments="slotProps">
            <span
              v-if="team.phone_screen_user_id == slotProps.row.id"
              class="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-blue-100 text-blue-800"
            >
              Assigned to Phone Screen
            </span>
            <span
              v-if="team.interview_user_id == slotProps.row.id"
              class="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-green-100 text-green-800"
            >
              Assigned to Interview
            </span>
            <span
              v-if="team.esigner_user_id == slotProps.row.id"
              class="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-indigo-100 text-indigo-800"
            >
              Assigned to E-signatures
            </span>
          </template>
          <template #td-status="slotProps">
            <span
              v-if="slotProps.row.invited"
              class="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-yellow-100 text-yellow-800"
            >
              Invited
            </span>
            <span
              v-else-if="slotProps.row.status == 'active'"
              class="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-green-100 text-green-800"
            >
              Active
            </span>
            <span v-else class="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-red-100 text-red-800">
              Inactive
            </span>
          </template>
          <template #td-action="slotProps">
            <ButtonMenu :icon-only="true" :sections="3">
              <template #topmenu>
                <a
                  v-if="slotProps.row.invited"
                  class="cursor-pointer group flex items-center px-4 py-2 text-sm leading-5 text-gray-700 hover:bg-gray-100 hover:text-gray-900 focus:outline-none focus:bg-gray-100 focus:text-gray-900"
                  role="menuitem"
                  @click="resendInvite(slotProps.row.id)"
                >
                  <vue-feather
                    type="mail"
                    class="mr-3 h-5 w-5 text-gray-400 group-hover:text-gray-500 group-focus:text-gray-500"
                  />
                  Resend Invite
                </a>
                <a
                  v-if="slotProps.row.role == 'Contributor'"
                  class="cursor-pointer group flex items-center px-4 py-2 text-sm leading-5 text-gray-700 hover:bg-gray-100 hover:text-gray-900 focus:outline-none focus:bg-gray-100 focus:text-gray-900"
                  role="menuitem"
                  @click="updateUser('role', 'Admin', slotProps.row.id)"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke-width="2"
                    stroke="currentColor"
                    class="mr-3 h-5 w-5 text-gray-400 group-hover:text-gray-500 group-focus:text-gray-500"
                  >
                    <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      d="M7.5 21L3 16.5m0 0L7.5 12M3 16.5h13.5m0-13.5L21 7.5m0 0L16.5 12M21 7.5H7.5"
                    />
                  </svg>
                  Change to Admin
                </a>
                <a
                  v-else
                  class="cursor-pointer group flex items-center px-4 py-2 text-sm leading-5 text-gray-700 hover:bg-gray-100 hover:text-gray-900 focus:outline-none focus:bg-gray-100 focus:text-gray-900"
                  role="menuitem"
                  @click="updateUser('role', 'Contributor', slotProps.row.id)"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke-width="2"
                    stroke="currentColor"
                    class="mr-3 h-5 w-5 text-gray-400 group-hover:text-gray-500 group-focus:text-gray-500"
                  >
                    <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      d="M7.5 21L3 16.5m0 0L7.5 12M3 16.5h13.5m0-13.5L21 7.5m0 0L16.5 12M21 7.5H7.5"
                    />
                  </svg>
                  Change to Contributor
                </a>
              </template>
              <template v-if="slotProps.row.role != 'Agent'" #middlemenu>
                <a
                  class="cursor-pointer group flex items-center px-4 py-2 text-sm leading-5 text-gray-700 hover:bg-gray-100 hover:text-gray-900 focus:outline-none focus:bg-gray-100 focus:text-gray-900"
                  role="menuitem"
                  @click="assignToPhoneScreen(slotProps.row.id)"
                >
                  <svg
                    class="mr-3 h-5 w-5 text-gray-400 group-hover:text-gray-500 group-focus:text-gray-500"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke-width="2"
                    stroke="currentColor"
                  >
                    <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      d="M2.25 6.75c0 8.284 6.716 15 15 15h2.25a2.25 2.25 0 002.25-2.25v-1.372c0-.516-.351-.966-.852-1.091l-4.423-1.106c-.44-.11-.902.055-1.173.417l-.97 1.293c-.282.376-.769.542-1.21.38a12.035 12.035 0 01-7.143-7.143c-.162-.441.004-.928.38-1.21l1.293-.97c.363-.271.527-.734.417-1.173L6.963 3.102a1.125 1.125 0 00-1.091-.852H4.5A2.25 2.25 0 002.25 4.5v2.25z"
                    />
                  </svg>
                  Assign to Phone Screen
                </a>
                <a
                  class="cursor-pointer group flex items-center px-4 py-2 text-sm leading-5 text-gray-700 hover:bg-gray-100 hover:text-gray-900 focus:outline-none focus:bg-gray-100 focus:text-gray-900"
                  role="menuitem"
                  @click="assignToInterview(slotProps.row.id)"
                >
                  <svg
                    class="mr-3 h-5 w-5 text-gray-400 group-hover:text-gray-500 group-focus:text-gray-500"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke-width="2"
                    stroke="currentColor"
                  >
                    <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      d="M13.5 21v-7.5a.75.75 0 01.75-.75h3a.75.75 0 01.75.75V21m-4.5 0H2.36m11.14 0H18m0 0h3.64m-1.39 0V9.349m-16.5 11.65V9.35m0 0a3.001 3.001 0 003.75-.615A2.993 2.993 0 009.75 9.75c.896 0 1.7-.393 2.25-1.016a2.993 2.993 0 002.25 1.016c.896 0 1.7-.393 2.25-1.016a3.001 3.001 0 003.75.614m-16.5 0a3.004 3.004 0 01-.621-4.72L4.318 3.44A1.5 1.5 0 015.378 3h13.243a1.5 1.5 0 011.06.44l1.19 1.189a3 3 0 01-.621 4.72m-13.5 8.65h3.75a.75.75 0 00.75-.75V13.5a.75.75 0 00-.75-.75H6.75a.75.75 0 00-.75.75v3.75c0 .415.336.75.75.75z"
                    />
                  </svg>
                  Assign to Interview
                </a>
                <a
                  class="cursor-pointer group flex items-center px-4 py-2 text-sm leading-5 text-gray-700 hover:bg-gray-100 hover:text-gray-900 focus:outline-none focus:bg-gray-100 focus:text-gray-900"
                  role="menuitem"
                  @click="assignToEsigner(slotProps.row.id)"
                >
                  <vue-feather
                    type="feather"
                    class="mr-3 h-5 w-5 text-gray-400 group-hover:text-gray-500 group-focus:text-gray-500"
                  />
                  Assign to E-signatures
                </a>
              </template>
              <template #bottommenu>
                <a
                  v-if="slotProps.row.status == 'active'"
                  class="cursor-pointer group flex items-center px-4 py-2 text-sm leading-5 text-gray-700 hover:bg-gray-100 hover:text-gray-900 focus:outline-none focus:bg-gray-100 focus:text-gray-900"
                  role="menuitem"
                  @click="updateUser('status', 'inactive', slotProps.row.id)"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke-width="2"
                    stroke="currentColor"
                    class="mr-3 h-5 w-5 text-gray-400 group-hover:text-gray-500 group-focus:text-gray-500"
                  >
                    <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      d="M16.5 10.5V6.75a4.5 4.5 0 10-9 0v3.75m-.75 11.25h10.5a2.25 2.25 0 002.25-2.25v-6.75a2.25 2.25 0 00-2.25-2.25H6.75a2.25 2.25 0 00-2.25 2.25v6.75a2.25 2.25 0 002.25 2.25z"
                    />
                  </svg>
                  Deactivate user
                </a>
                <a
                  v-else
                  class="cursor-pointer group flex items-center px-4 py-2 text-sm leading-5 text-gray-700 hover:bg-gray-100 hover:text-gray-900 focus:outline-none focus:bg-gray-100 focus:text-gray-900"
                  role="menuitem"
                  @click="updateUser('status', 'active', slotProps.row.id)"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke-width="2"
                    stroke="currentColor"
                    class="mr-3 h-5 w-5 text-gray-400 group-hover:text-gray-500 group-focus:text-gray-500"
                  >
                    <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      d="M13.5 10.5V6.75a4.5 4.5 0 119 0v3.75M3.75 21.75h10.5a2.25 2.25 0 002.25-2.25v-6.75a2.25 2.25 0 00-2.25-2.25H3.75a2.25 2.25 0 00-2.25 2.25v6.75a2.25 2.25 0 002.25 2.25z"
                    />
                  </svg>
                  Activate User
                </a>
              </template>
            </ButtonMenu>
          </template>
        </DataTable>
      </div>
      <div v-else>
        <LoadingList />
      </div>
    </div>
  </SettingsPageLayout>
</template>

<script>
import SettingsPageLayout from '../../SettingsPageLayout/index.vue';
import DetailForm from '../../shared/DetailForm.vue';
import LoadingList from '../../shared/LoadingList.vue';
import ButtonMenu from '../../shared/ButtonMenu.vue';
import DataTable from '../../shared/DataTable/index.vue';
import { formatStandardDateTime } from '../../../utilities/format.ts';
import { createUser } from '../../../api/users.ts';
import QuickstartCard from '../../shared/QuickstartCard.vue';
export default {
  components: {
    SettingsPageLayout,
    DetailForm,
    LoadingList,
    DataTable,
    ButtonMenu,
    QuickstartCard,
  },
  data() {
    return {
      user: {
        email: '',
        role: '',
      },
      team: null,
      users: [],
    };
  },
  computed: {
    dtColumns() {
      return [
        {
          label: 'Name',
          key: 'name',
          sortable: false,
        },
        {
          label: 'Email',
          key: 'email',
          sortable: false,
        },
        {
          label: 'Status',
          key: 'status',
          sortable: false,
        },
        {
          label: 'Role',
          key: 'role',
          sortable: false,
        },
        {
          label: 'Assignments',
          key: 'assignments',
          sortable: false,
        },
        {
          label: '# of Logins',
          key: 'sign_in_count',
          sortable: false,
        },
        {
          label: 'Created At',
          key: 'created_at',
          formatter: formatStandardDateTime,
          sortable: false,
        },
        {
          label: 'Action',
          key: 'action',
          sortable: false,
        },
      ];
    },
    rows() {
      return this.users;
    },
    sort() {
      return {
        key: 'created_at',
        order: 'desc',
      };
    },
  },
  mounted() {
    this.getUsers();
  },
  methods: {
    getUsers() {
      const self = this;
      fetch(`/api/teams/1`)
        .then(response => {
          return response.json();
        })
        .then(data => {
          self.users = data.team_members;
          self.team = data.record;
        });
    },
    async createUser() {
      this.$store.commit('general/showLocalLoadingSpinner');
      await createUser({
        email: this.user.email,
        role: this.user.role,
      });

      this.getUsers();
      this.user.email = '';
      this.$store.commit('general/hideLocalLoadingSpinner');
      this.$store.commit('general/showAlert', {
        alertType: 'success',
        alertTitle: 'Team Member Created',
        alertDescription: 'Your New Team Member was successfully created.  Use the button below to dismiss this alert.',
      });
    },
    async updateUser(column, e, userId) {
      this.$store.commit('general/showLocalLoadingSpinner');
      let body = {};
      body[column] = e;
      await this.$store.dispatch('general/updateUser', {
        user: { id: userId },
        updatedUserAttributes: body,
      });
      this.getUsers();
      this.$store.commit('general/hideLocalLoadingSpinner');
      this.$store.commit('general/showAlert', {
        alertType: 'success',
        alertTitle: 'User updated',
        alertDescription: 'Your User was successfully updated.  Use the button below to dismiss this alert.',
      });
    },
    async resendInvite(userId) {
      this.$store.commit('general/showLocalLoadingSpinner');
      await this.$store.dispatch('general/updateUser', {
        user: { id: userId },
        updatedUserAttributes: { resend_invite: true },
      });
      this.getUsers();
      this.$store.commit('general/hideLocalLoadingSpinner');
      this.$store.commit('general/showAlert', {
        alertType: 'success',
        alertTitle: 'Invite resent!',
        alertDescription: 'Your user invite was successfully resent.  Use the button below to dismiss this alert.',
      });
    },
    assignToPhoneScreen(userId) {
      const self = this;
      self.$store.commit('general/showLocalLoadingSpinner');
      fetch(`/api/teams/${self.team.id}`, {
        method: 'PUT',
        body: JSON.stringify({
          phone_screen_user_id: userId,
        }),
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-TOKEN': document.querySelectorAll('[name="csrf-token"]')[0].content,
        },
      })
        .then(response => {
          return response.json();
        })
        .then(data => {
          self.$store.commit('general/hideLocalLoadingSpinner');
          self.getUsers();
          if (data.status != 'error') {
            self.$store.commit('general/showAlert', {
              alertType: 'success',
              alertTitle: 'Phone screen user updated',
              alertDescription: 'Your team was successfully updated.  Use the button below to dismiss this alert.',
            });
          } else {
            self.$store.commit('general/showAlert', {
              alertType: 'error',
              alertTitle: 'Phone screen update error',
              alertDescription: data.message,
            });
          }
        });
    },
    assignToInterview(userId) {
      const self = this;
      self.$store.commit('general/showLocalLoadingSpinner');
      fetch(`/api/teams/${self.team.id}`, {
        method: 'PUT',
        body: JSON.stringify({
          interview_user_id: userId,
        }),
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-TOKEN': document.querySelectorAll('[name="csrf-token"]')[0].content,
        },
      })
        .then(response => {
          return response.json();
        })
        .then(data => {
          self.$store.commit('general/hideLocalLoadingSpinner');
          self.getUsers();
          if (data.status != 'error') {
            self.$store.commit('general/showAlert', {
              alertType: 'success',
              alertTitle: 'Interview user updated',
              alertDescription: 'Your team was successfully updated.  Use the button below to dismiss this alert.',
            });
          } else {
            self.$store.commit('general/showAlert', {
              alertType: 'error',
              alertTitle: 'Interview update error',
              alertDescription: data.message,
            });
          }
        });
    },
    assignToPipelineReviews(userId) {
      const self = this;
      self.$store.commit('general/showLocalLoadingSpinner');
      fetch(`/api/teams/${self.team.id}`, {
        method: 'PUT',
        body: JSON.stringify({
          pipeline_reviews_user_id: userId,
        }),
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-TOKEN': document.querySelectorAll('[name="csrf-token"]')[0].content,
        },
      })
        .then(response => {
          return response.json();
        })
        .then(data => {
          self.$store.commit('general/hideLocalLoadingSpinner');
          self.getUsers();
          if (data.status != 'error') {
            self.$store.commit('general/showAlert', {
              alertType: 'success',
              alertTitle: 'Pipeline Reviews user updated',
              alertDescription: 'Your team was successfully updated.  Use the button below to dismiss this alert.',
            });
          } else {
            self.$store.commit('general/showAlert', {
              alertType: 'error',
              alertTitle: 'Pipeline Reviews update error',
              alertDescription: data.message,
            });
          }
        });
    },
    assignToEsigner(userId) {
      const self = this;
      self.$store.commit('general/showLocalLoadingSpinner');
      fetch(`/api/teams/${self.team.id}`, {
        method: 'PUT',
        body: JSON.stringify({
          esigner_user_id: userId,
        }),
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-TOKEN': document.querySelectorAll('[name="csrf-token"]')[0].content,
        },
      })
        .then(response => {
          return response.json();
        })
        .then(data => {
          self.$store.commit('general/hideLocalLoadingSpinner');
          self.getUsers();
          if (data.status != 'error') {
            self.$store.commit('general/showAlert', {
              alertType: 'success',
              alertTitle: 'E-signature user updated',
              alertDescription: 'Your team was successfully updated.  Use the button below to dismiss this alert.',
            });
          } else {
            self.$store.commit('general/showAlert', {
              alertType: 'error',
              alertTitle: 'E-signature user update error',
              alertDescription: data.message,
            });
          }
        });
    },
  },
};
</script>
